import * as React from 'react';
import {useMemo, useCallback, useState, useRef} from 'react';
import {
  dynamic,
  assignProps,
  createView,
  view,
  createViewConfig,
  toIntent,
  type Intent,
  type Merge,
} from '@sail/engine';
import type {FocusManager} from '@sail/react-aria';
import {
  FocusScope,
  useControlledState,
  useFocusManager,
  useSpinButton,
} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';
import {css} from '../css';
import type {View} from '../view';
import type {FormFieldProps} from './FormField';
import {NativeTextFieldConfig, TextField} from './TextField';
import type {CompositeFieldProps} from './CompositeField';
import {CompositeField} from './CompositeField';
import {composite, rovingTabIndex} from './rovingTabIndex';

type DateStyle = 'YMD' | 'MDY' | 'DMY' | 'YM' | 'MY' | 'MD' | 'DM';
type DateStyleKey = 'Y' | 'M' | 'D';

// This is rough, and we don't want it to change after page load
const now = new Date();

// TODO: Fix this for various different calendar formats.
// e.g. Ethiopic Calendar has 13 months, Hebrew Calendar has 12 or 13 months depending on the year.
// Should probably use a intl library for this.
const minMaxStartPerDateStyle = (
  t: DateStyleKey,
): {min: number; max: number; start: number} => {
  if (t === 'Y') {
    const currentYear = now.getFullYear();
    return {
      min: 1,
      max: currentYear + 100,
      start: currentYear - 1,
    };
  } else if (t === 'M') {
    const currentMonth = now.getMonth();
    return {
      min: 1,
      max: 12,
      start: currentMonth,
    };
  } else if (t === 'D') {
    const currentDay = now.getDate();
    return {
      min: 1,
      max: 31,
      start: currentDay - 1,
    };
  }
  return {
    min: 0,
    max: 100,
    start: 0,
  };
};

type DateString = string | undefined;

export type DateFieldProps = Merge<
  FormFieldProps<Omit<CompositeFieldProps, 'onChange'>>,
  {
    /**
     * Programmatically mark the value as invalid
     * @external
     */
    invalid?: boolean;
    /**
     * A default value that a user can change
     * @external
     */
    defaultValue: string;
    /**
     * Handler that is called when a valid date is entered into the field
     * @external
     */
    onDateChange?: (value: string) => void;
    /**
     * Handler that is called for each modification to the field, irrespective of validity
     */
    onChange?: (value: string) => void;
    /**
     * Accepts an object with keys for each field, where values are callbacks that return a string value.
     * Fires on each change to the field, irrespective of validity.
     * @external
     */
    unstable_onFieldChange?: Partial<
      Record<DateStyleKey, (value: string) => void>
    >;
    /**
     * Sets the value directly, cannot be changed by a user
     */
    value?: string;
    /**
     * Descriptive text that will be rendered adjacent to the control's label
     * @external
     */
    description?: React.ReactNode;
    /**
     * @external
     */
    disabled?: boolean;
    /**
     * The size of the input
     * @external
     */
    size: 'small' | 'medium' | 'large';
    /**
     * Order that the year, month, and day fields are displayed
     */
    format: 'YMD' | 'MDY' | 'DMY' | 'YM' | 'MY' | 'MD' | 'DM';
    /**
     * Indicated whether to add leading 0's to each field
     * @external
     */
    shouldPadZeros?: Partial<Record<DateStyleKey, boolean>>;
    /**
     * The element to use as a separator between date fields
     */
    separator?: React.ReactNode;
    /**
     * Placeholder text for each date segment field
     */
    placeholders?: Partial<Record<DateStyleKey, string>>;
  }
>;

const Separator = createView(({separator}: {separator: React.ReactNode}) => {
  if (typeof separator === 'string') {
    return (
      <view.span
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        uses={[assignProps({'aria-hidden': true} as any)]}
        css={{bleedX: 'space.75'}}
      >
        {separator || '/'}
      </view.span>
    );
  }
  return separator ? <>{separator}</> : null;
});

function cleanNonDigits(v: string): string {
  return v.replace(/\D/g, '');
}

function trimLeadingZeros(v: string): string {
  return v.replace(/^0+/, '');
}

function padLeadingZeros(len: number, v: string): string {
  return v.padStart(len, '0');
}

function formatSingleValue(
  type: DateStyleKey,
  value: string,
  hasManualZero: boolean,
  shouldPadZeros = true,
): string {
  const datePartLength = type === 'Y' ? 4 : 2;
  const noDigits = cleanNonDigits(value);
  const noLeadingZeros = hasManualZero ? noDigits : trimLeadingZeros(noDigits);
  const trimmedToLength = noLeadingZeros.substring(0, datePartLength);
  let formattedValue =
    hasManualZero || !shouldPadZeros
      ? trimmedToLength
      : padLeadingZeros(datePartLength, trimmedToLength);

  // If the value is all zeros, then clear it out
  if (formattedValue.length === datePartLength && /^0+$/.test(formattedValue)) {
    formattedValue = '';
  }

  return formattedValue;
}

// This validates that the actual date has or will occur
const isActualDate = (
  d: Date,
  valueMap: {[k in DateStyleKey]?: string},
): boolean => {
  const {Y = '', M = '', D = ''} = valueMap;
  const UTCVal = {
    Y: padLeadingZeros(4, String(d.getUTCFullYear())),
    M: padLeadingZeros(2, String(d.getUTCMonth() + 1)),
    D: padLeadingZeros(2, String(d.getUTCDate())),
  };
  if (Y !== UTCVal.Y || M !== UTCVal.M || D !== UTCVal.D) {
    return false;
  }
  return true;
};

// The checks if the date object was constructed at all
// However, just because the date object was constructed it
// doesn't mean the date is an *actual* date. e.g. 2013-02-30
function isValidDateObject(d: Date): boolean {
  return d instanceof Date && !isNaN(d as unknown as number);
}

function formatFullValue({Y = '', M = '', D = ''}): string {
  return [Y, M, D].join('-');
}

function formatChangedValue(value: DateValue, format: DateStyle): string {
  return (['Y', 'M', 'D'] as DateStyleKey[])
    .filter((type: DateStyleKey) => format.includes(type))
    .map((type: DateStyleKey) => value[type])
    .join('-');
}

function isDatePartLikelyComplete(
  t: DateStyleKey,
  value: string,
  hasManualZero: boolean,
): boolean {
  const datePartLength = t === 'Y' ? 4 : 2;

  // If it's the correct amount of characters AND doesn't start with zero
  return (
    value.length === datePartLength &&
    (value.charAt(0) !== '0' || hasManualZero)
  );
}

export const DateFieldConfig = createViewConfig({
  props: {} as DateFieldProps,
  name: 'TextField',
  flattens: true,
  defaults: {
    size: 'medium',
    separator: '/',
    defaultValue: '',
    format: 'YMD',
  },
});

function isValidDateString(dateString: string, format: string): boolean {
  const splitValues = dateString.split('-');
  return dateString.length > 0 && splitValues.length === format.length;
}

function parseDate(dateString: string, format: DateStyle): DateValue {
  if (!isValidDateString(dateString, format)) {
    return {
      D: '',
      M: '',
      Y: '',
    };
  }

  // Possible dateString value formats
  // Y-M-D
  // Y-M
  // M-D
  const splitValues = dateString.split('-');

  switch (format) {
    case 'YM':
    case 'MY':
      return {
        D: '01',
        M: splitValues[1] || '',
        Y: splitValues[0] || '',
      };
    case 'MD':
    case 'DM':
      return {
        D: splitValues[1] || '',
        M: splitValues[0] || '',
        Y: '2024', // 2024 is a leap year and it will work for any date
      };
    default:
      return {
        D: splitValues[2] || '',
        M: splitValues[1] || '',
        Y: splitValues[0] || '',
      };
  }
}

type SegmentProps = React.ComponentProps<typeof TextField>;

const inputMap = {
  Y: {
    // TODO: SAIL-3215 no hardcoded strings
    label: 'Year',
    placeholder: 'YYYY',
    pattern: '[0-9]{4}',
  },
  M: {
    // TODO: SAIL-3215 no hardcoded strings
    label: 'Month',
    placeholder: 'MM',
    pattern: '[0-1][0-9]',
  },
  D: {
    // TODO: SAIL-3215 no hardcoded strings
    label: 'Day',
    placeholder: 'DD',
    pattern: '[0-3][0-9]',
  },
};

type DateValue = {D: string; M: string; Y: string};
type ZeroPad = {D: boolean; M: boolean; Y: boolean};

type DateFieldState = {
  value: DateString;
  dateParts: DateValue;
  setSegment(key: DateStyleKey, value: string): void;
  increment(key: DateStyleKey): void;
  decrement(key: DateStyleKey): void;
  manualZero: ZeroPad;
  setManualZero(manualZero: ZeroPad): void;
  props: Pick<DateFieldProps, 'shouldPadZeros' | 'placeholders'>;
};

function useDateSegment(
  type: DateStyleKey,
  state: DateFieldState,
): View.Intent<Partial<SegmentProps>> {
  const value = state.dateParts[type];
  const minMaxStart = minMaxStartPerDateStyle(type);
  const {shouldPadZeros} = state.props;
  const focusManager = useRef<FocusManager>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      let hasManualZero = state.manualZero[type];
      if (val === '0') {
        hasManualZero = true;
        state.setManualZero({...state.manualZero, [type]: true});
      } else if (val === '' || val.length === 1) {
        hasManualZero = false;
        state.setManualZero({...state.manualZero, [type]: false});
      }

      const formattedValue = formatSingleValue(
        type,
        val,
        hasManualZero,
        shouldPadZeros?.[type],
      );

      state.setSegment(type, formattedValue);

      if (isDatePartLikelyComplete(type, formattedValue, hasManualZero)) {
        focusManager.current?.focusNext({
          from: e.target as HTMLElement,
          tabbable: true,
        });
      }
    },
    [state, type, shouldPadZeros, focusManager],
  );

  const {spinButtonProps} = useSpinButton({
    value: value ? parseInt(value, 10) : undefined,
    textValue: value || '',
    minValue: minMaxStart.min,
    maxValue: minMaxStart.max,
    onIncrement: () => state.increment(type),
    onDecrement: () => state.decrement(type),
    onIncrementToMax: () => state.setSegment(type, `${minMaxStart.max}`),
    onDecrementToMin: () => state.setSegment(type, `${minMaxStart.min}`),
  });

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const el = e.target as HTMLInputElement;

      if (e.key === 'Backspace' && el.value === '') {
        focusManager.current?.focusPrevious({
          from: el,
          tabbable: true,
        });
      }
    },
    [focusManager],
  );

  const onFocus = useCallback((e: FocusEvent) => {
    const el = e.target as HTMLInputElement;

    if (el.value?.length > 0) {
      el.setSelectionRange(0, 4);
    }
  }, []);

  return toIntent([
    dynamic(function DateSegment() {
      const current = useFocusManager();
      // use the FocusScope's focus manager to enable programmatic control over moving focus
      Object.assign(focusManager, {current});
    }),
    assignProps<SegmentProps>({
      value,
      inputMode: 'numeric',
      hiddenElements: ['label'],
      onChange: handleChange,
      ...inputMap[type],
      placeholder:
        state.props.placeholders?.[type] || inputMap[type].placeholder,
      ...spinButtonProps,
    }),
    assignProps<SegmentProps>({
      // select the whole input value when the input is clicked
      onClick: (event: React.MouseEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;

        // eslint-disable-next-line @sail/ui-no-reflow
        target.select();
      },
      // prevent the browser from changing the caret position
      onMouseUp: (event: React.MouseEvent<HTMLInputElement>) => {
        event.preventDefault();
      },
      // select the whole input value when the input is focused
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        // eslint-disable-next-line @sail/ui-no-reflow
        event.target.select();
      },
    }),
    rovingTabIndex as Intent,
    // roving tab index will remove the element from the tab sequence
    // but date field is a bit of a special case where we still enable users to tab to each segment.
    assignProps({tabIndex: 0}),
    assignProps({onFocus, onKeyDown}) as Intent,
    value.length === 0 ? css({caretColor: 'transparent'}) : css({}),
  ]);
}

function useDateFieldState(props: Partial<DateFieldProps>): DateFieldState {
  const [value, setValue] = useControlledState<DateString>(
    props.value,
    props.defaultValue,
    // NOTE: useControlledState optional arg, but it's not reflected in the type definition
    props.onChange as (value: DateString) => void,
  );

  const dateParts = useMemo(
    () => parseDate(value || '', props.format!),
    [value, props.format],
  );

  const [manualZero, setManualZero] = useState({
    D: dateParts.D.startsWith('0'),
    M: dateParts.M.startsWith('0'),
    Y: dateParts.Y.startsWith('0'),
  });

  const {onDateChange, unstable_onFieldChange} = props;

  const setSegment = useCallback(
    (key: DateStyleKey, value: string) => {
      const newValue = {...dateParts, [key]: value};
      const formatted = formatChangedValue(newValue, props.format!);
      // update the value of the control to reflect the formatted value
      // triggering change event as needed
      setValue(formatted);

      if (unstable_onFieldChange) {
        unstable_onFieldChange[key]?.(value);
      }

      const d = new Date(formatFullValue(newValue));

      if (isValidDateObject(d) && isActualDate(d, newValue)) {
        onDateChange?.(formatted);
      }
    },
    [dateParts, onDateChange, unstable_onFieldChange, setValue, props.format],
  );

  /**
   * Modifies the a specific segment of the date by the given amount,
   * cycling the value to the min/max if needed.
   */
  const adjustSegment = useCallback(
    (key: DateStyleKey, amount: number) => {
      const minMaxStart = minMaxStartPerDateStyle(key);
      const currentString = dateParts[key];
      const currentValue = currentString
        ? parseInt(cleanNonDigits(currentString), 10)
        : minMaxStart.start;

      let nextValue = currentValue + amount;

      if (nextValue < minMaxStart.min) {
        nextValue = minMaxStart.max;
      }
      if (nextValue > minMaxStart.max) {
        nextValue = minMaxStart.min;
      }

      const formatted = formatSingleValue(key, `${nextValue}`, false, true);

      setSegment(key, formatted);
    },
    [dateParts, setSegment],
  );

  const increment = useCallback(
    (key: DateStyleKey) => adjustSegment(key, 1),
    [adjustSegment],
  );
  const decrement = useCallback(
    (key: DateStyleKey) => adjustSegment(key, -1),
    [adjustSegment],
  );

  return {
    value,
    dateParts,
    setSegment,
    increment,
    decrement,
    manualZero,
    setManualZero,
    props,
  };
}

const SegmentTextField: React.FC<{
  dateType: DateStyleKey;
  state: DateFieldState;
}> = ({dateType, state}) => {
  const segment = useDateSegment(dateType as DateStyleKey, state);

  // labels are provided via date segment intent
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  return <TextField label="" uses={[segment]} />;
};

export const DateField = DateFieldConfig.createView(
  ({
    defaultValue,
    value,
    format,
    separator,
    onDateChange,
    onChange,
    unstable_onFieldChange,
    placeholders,
    shouldPadZeros,
    ...props
  }) => {
    const state = useDateFieldState({
      defaultValue,
      value,
      format,
      placeholders,
      onDateChange,
      onChange,
      unstable_onFieldChange,
      shouldPadZeros,
    });

    const dateFormatKeys = format.split('') as DateStyleKey[];

    return (
      <CompositeField
        {...(props as CompositeFieldProps)}
        uses={[composite as Intent]}
      >
        <FocusScope>
          {dateFormatKeys.map((type, index) => {
            return (
              <React.Fragment key={type}>
                <SegmentTextField dateType={type} state={state} />
                {index < dateFormatKeys.length - 1 ? (
                  <Separator separator={separator} />
                ) : null}
              </React.Fragment>
            );
          })}
        </FocusScope>
      </CompositeField>
    );
  },
  {
    subviews: {
      inputs: {
        css: {alignX: 'start', width: 'fit'},
        uses: [
          NativeTextFieldConfig.customize({
            uses: [
              css({
                fontVariantNumeric: 'tabular-nums',
              }),
              css({
                textAlign: 'center',

                ':focus': {
                  borderRadius: 'xsmall',
                  backgroundColor: $['color.feedback.info.100'],
                },

                '::selection': {
                  backgroundColor: $['color.feedback.info.100'],
                },
              }),
            ],
            forward: {
              placeholder: true,
            },
            variants: {
              pattern: {
                [inputMap.Y.pattern]: {css: {width: '3em'}},
                [inputMap.M.pattern]: {css: {width: '2em'}},
                [inputMap.D.pattern]: {css: {width: '2em'}},
              },
            },
          }),
        ],
      },
    },
  },
);
